'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { BreadcrumbItem } from '@/shared/components/ui/common/BreadcrumbsLegacy';
import { isNumeroNotificacao } from '@/shared/utils/numeroNotificacao';
interface LayoutState {
  isSidebarCollapsed: boolean;
  isMobileMenuOpen: boolean;
  isLoading: boolean;
}
interface RouteConfig {
  title: string;
  breadcrumbs: BreadcrumbItem[];
}
const routeConfigs: Record<string, RouteConfig> = {
  '/': {
    title: 'Dashboard',
    breadcrumbs: [{
      label: 'Dashboard',
      current: true
    }]
  },
  '/execucoes': {
    title: 'Execuções',
    breadcrumbs: [{
      label: 'Execuções',
      path: '/execucoes',
      current: true
    }]
  },
  '/categorias': {
    title: 'Categorias',
    breadcrumbs: [{
      label: 'Categorias',
      path: '/categorias',
      current: true
    }]
  },
  '/peticionamento/novo': {
    title: 'Novo peticionamento inicial',
    breadcrumbs: [{
      label: 'Início',
      path: '/'
    }, {
      label: 'Novo peticionamento inicial',
      path: '/peticionamento/novo',
      current: true
    }]
  },
  '/peticionamento': {
    title: 'Peticionamento inicial',
    breadcrumbs: [{
      label: 'Início',
      path: '/'
    }, {
      label: 'Peticionamento inicial',
      path: '/peticionamento',
      current: true
    }]
  },
  '/relatorios': {
    title: 'Relatórios',
    breadcrumbs: [{
      label: 'Relatórios',
      path: '/relatorios',
      current: true
    }]
  },
  '/tribunais': {
    title: 'Tribunais',
    breadcrumbs: [{
      label: 'Início',
      path: '/'
    }, {
      label: 'Tribunais',
      path: '/tribunais',
      current: true
    }]
  },
  '/usuarios': {
    title: 'Usuários',
    breadcrumbs: [{
      label: 'Início',
      path: '/'
    }, {
      label: 'Usuários',
      path: '/usuarios',
      current: true
    }]
  },
  '/configuracoes': {
    title: 'Configurações',
    breadcrumbs: [{
      label: 'Início',
      path: '/'
    }, {
      label: 'Configurações',
      path: '/configuracoes',
      current: true
    }]
  },
  '/configuracoes/chaves-de-api': {
    title: 'Chaves de API',
    breadcrumbs: [{
      label: 'Início',
      path: '/'
    }, {
      label: 'Configurações',
      path: '/configuracoes'
    }, {
      label: 'Chaves de API',
      path: '/configuracoes/chaves-de-api',
      current: true
    }]
  },
  '/configuracoes/integracoes': {
    title: 'Integrações',
    breadcrumbs: [{
      label: 'Início',
      path: '/'
    }, {
      label: 'Configurações',
      path: '/configuracoes'
    }, {
      label: 'Integrações',
      path: '/configuracoes/integracoes',
      current: true
    }]
  },
  '/negociacoes/simulacao': {
    title: 'Testar meus agentes',
    breadcrumbs: [{
      label: 'Início',
      path: '/'
    }, {
      label: 'Negociações',
      path: '/negociacoes'
    }, {
      label: 'Testar meus agentes',
      path: '/negociacoes/simulacao',
      current: true
    }]
  },
  '/painel-advogado': {
    title: 'Painel do Advogado',
    breadcrumbs: [{
      label: 'Início',
      path: '/'
    }, {
      label: 'Painel do Advogado',
      path: '/painel-advogado',
      current: true
    }]
  }
};
const PREFIXO_DA_ASSINATURA_DE_TESTE = '/negociacoes/simulacao/assinatura/';
const assinaturaDeTesteConfig: RouteConfig = {
  title: 'Assinatura de teste',
  breadcrumbs: [{
    label: 'Início',
    path: '/'
  }, {
    label: 'Negociações',
    path: '/negociacoes'
  }, {
    label: 'Testar meus agentes',
    path: '/negociacoes/simulacao'
  }, {
    label: 'Assinatura de teste',
    current: true
  }]
};
const generateDynamicBreadcrumbs = (path: string): BreadcrumbItem[] => {
  const segments = path.split('/').filter(Boolean);
  const breadcrumbs: BreadcrumbItem[] = [];
  if (path !== '/') {
    breadcrumbs.push({
      label: 'Início',
      path: '/'
    });
  }
  let currentPath = '';
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const isLast = index === segments.length - 1;
    if (isLast && segments[index - 1] === 'notificacoes' && isNumeroNotificacao(segment)) {
      return;
    }
    if (segment === 'acompanhamento') {
      return;
    }
    const config = routeConfigs[currentPath];
    let label = config?.title || segment.charAt(0).toUpperCase() + segment.slice(1);
    if (segment === 'novo') label = 'Novo';
    if (segment === 'peticionamento' && !isLast) label = 'Peticionamento inicial';
    if (segment === 'painel-advogado') label = 'Painel do Advogado';
    if (segment.match(/^\d+$/)) label = `#${segment}`;
    breadcrumbs.push({
      label,
      path: currentPath,
      current: isLast
    });
  });
  return breadcrumbs;
};
export const useLayout = () => {
  const $ = _c(17);
  const pathname = usePathname();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      isSidebarCollapsed: false,
      isMobileMenuOpen: false,
      isLoading: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [state, setState] = useState(t0);
  const manuallyCollapsedRef = useRef(false);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setState(prev => {
        const newCollapsedState = !prev.isSidebarCollapsed;
        manuallyCollapsedRef.current = newCollapsedState;
        return {
          ...prev,
          isSidebarCollapsed: newCollapsedState
        };
      });
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const toggleSidebar = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setState(_temp);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const toggleMobileMenu = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setState(_temp2);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const closeMobileMenu = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = isLoading => {
      setState(prev_2 => ({
        ...prev_2,
        isLoading
      }));
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const setLoading = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      closeMobileMenu();
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== pathname) {
    t6 = [pathname, closeMobileMenu];
    $[6] = pathname;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[8] !== pathname) {
    t7 = () => {
      const isCreationRoute = pathname.startsWith("/peticionamento") || pathname.startsWith("/negociacoes/nova") || pathname.startsWith("/notificacoes/criar") || pathname.startsWith("/contratos/novo") || pathname.startsWith("/contratos/") && pathname !== "/contratos" && !pathname.includes("/contratos?");
      if (isCreationRoute) {
        manuallyCollapsedRef.current = false;
        setState(_temp3);
      } else {
        if (!manuallyCollapsedRef.current) {
          setState(_temp4);
        }
      }
    };
    t8 = [pathname];
    $[8] = pathname;
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  useEffect(t7, t8);
  const getRouteConfig = _temp5;
  let t9;
  if ($[11] !== pathname) {
    t9 = getRouteConfig(pathname);
    $[11] = pathname;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const currentRoute = t9;
  let t10;
  if ($[13] !== currentRoute || $[14] !== pathname || $[15] !== state) {
    t10 = {
      ...state,
      pathname,
      currentRoute,
      toggleSidebar,
      toggleMobileMenu,
      closeMobileMenu,
      setLoading
    };
    $[13] = currentRoute;
    $[14] = pathname;
    $[15] = state;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  return t10;
};
function _temp(prev_0) {
  return {
    ...prev_0,
    isMobileMenuOpen: !prev_0.isMobileMenuOpen
  };
}
function _temp2(prev_1) {
  return {
    ...prev_1,
    isMobileMenuOpen: false
  };
}
function _temp3(prev_3) {
  return {
    ...prev_3,
    isSidebarCollapsed: true
  };
}
function _temp4(prev_4) {
  return {
    ...prev_4,
    isSidebarCollapsed: false
  };
}
function _temp5(path) {
  if (routeConfigs[path]) {
    return routeConfigs[path];
  }
  if (path.startsWith(PREFIXO_DA_ASSINATURA_DE_TESTE)) {
    return assinaturaDeTesteConfig;
  }
  const dynamicBreadcrumbs = generateDynamicBreadcrumbs(path);
  if (dynamicBreadcrumbs.length > 0) {
    return {
      title: dynamicBreadcrumbs[dynamicBreadcrumbs.length - 1].label,
      breadcrumbs: dynamicBreadcrumbs
    };
  }
  return {
    title: "P\xE1gina",
    breadcrumbs: [{
      label: "P\xE1gina",
      current: true
    }]
  };
}